import { isEmpty } from '@ember/utils';
import { attr } from '@ember-data/model';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import EmberDataBaseFragment from '@square/dashboard-data/models/ember-data-base-fragment';
import { inject as service } from '@ember/service';
import { copy } from 'ember-copy';
import moment from 'moment';
function toMoment(date) {
  return moment(date, 'YYYY-MM-DD');
}
export default EmberDataBaseFragment.extend({
  store: service(),
  startDate: attr('string'),
  endDate: attr('string'),
  timePeriods: fragmentArray('special-hours-time-period'),
  daysOfWeek: fragmentArray('business-hours/DayOfWeek'),
  reason: attr('string'),
  isClosed: attr('boolean'),
  isNotDefined() {
    return !(this.startDate && this.endDate);
  },
  copyFrom(otherSpecialHour) {
    this.set('startDate', otherSpecialHour.startDate);
    this.set('endDate', otherSpecialHour.endDate);
    if (isEmpty(otherSpecialHour.daysOfWeek)) {
      this.set('daysOfWeek', []);
    } else {
      this.set('daysOfWeek', otherSpecialHour.daysOfWeek.map(day => copy(day)));
    }
    if (isEmpty(otherSpecialHour.timePeriods)) {
      this.set('timePeriods', []);
    } else {
      this.set('timePeriods', otherSpecialHour.timePeriods.map(period => copy(period)));
    }
    this.set('reason', otherSpecialHour.reason);
    this.set('isClosed', otherSpecialHour.isClosed);
  },
  timesToJSON() {
    const times = this.timePeriods.map(timePeriod => ({
      startTime: timePeriod.startLocalTime,
      endTime: timePeriod.endLocalTime
    }));
    return JSON.stringify(times);
  },
  doesOverlapWith(otherSpecialHour) {
    const thisStart = toMoment(this.startDate);
    const otherStart = toMoment(otherSpecialHour.startDate);
    const thisEnd = toMoment(this.endDate);
    const otherEnd = toMoment(otherSpecialHour.endDate);
    return otherStart.isSameOrBefore(thisStart) && otherEnd.isSameOrAfter(thisStart) || otherStart.isSameOrAfter(thisStart) && otherStart.isSameOrBefore(thisEnd);
  },
  sortTimePeriods(timePeriods = this.timePeriods) {
    const sortedTimePeriods = timePeriods.toArray().sort((a, b) => {
      const aStart = a.getLocalStartTimeMinutes();
      const bStart = b.getLocalStartTimeMinutes();
      if (aStart < bStart) {
        return -1;
      }
      if (aStart > bStart) {
        return 1;
      }
      return 0;
    });
    return sortedTimePeriods;
  },
  timePeriodsSplitMidnight() {
    if (this.timePeriods.length < 2) {
      return false;
    }
    const timePeriods = this.sortTimePeriods(this.timePeriods);
    const firstTimePeriod = timePeriods[0];
    const lastTimePeriod = timePeriods[timePeriods.length - 1];
    return firstTimePeriod.startLocalTime.hour === 0 && firstTimePeriod.startLocalTime.minute === 0 && lastTimePeriod.endLocalTime.hour === 0 && lastTimePeriod.endLocalTime.minute === 0;
  },
  // Times coming from Roster are split at midnight
  // This combines those periods for clearer UI representation
  getTimesCombinedAcrossMidnight() {
    const timePeriodsArray = this.sortTimePeriods();
    if (!this.timePeriodsSplitMidnight()) {
      return timePeriodsArray;
    }
    const firstTimePeriod = timePeriodsArray.shift();
    const lastTimePeriod = timePeriodsArray.pop();
    timePeriodsArray.push(this.store.createFragment('special-hours-time-period', {
      startLocalTime: lastTimePeriod.startLocalTime,
      endLocalTime: firstTimePeriod.endLocalTime
    }));
    return timePeriodsArray;
  },
  toMoments() {
    return {
      startMoment: toMoment(this.startDate),
      endMoment: toMoment(this.endDate)
    };
  }
}).reopenClass({
  createFromDatesAndTimesAndReason(store, startDateMoment, endDateMoment, times, reason, isClosed) {
    const startDate = startDateMoment.format('YYYY-MM-DD');
    const endDate = endDateMoment.format('YYYY-MM-DD');
    const timePeriods = times.map(time => {
      const startLocalTime = time.startLocalTime;
      const endLocalTime = time.endLocalTime;
      return store.createFragment('special-hours-time-period', {
        startLocalTime,
        endLocalTime
      });
    });
    return store.createFragment('special-hours', {
      startDate,
      endDate,
      timePeriods,
      reason,
      isClosed
    });
  },
  _modelNameForMigration: 'special-hours'
});